<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("admin.manage_organizers.title") }}</h1>
                </div>
            </div>

            <template v-if="initDone">
                <div class="columns">
                    <div class="column">
                        <p class="mb-3">
                            {{ translate("admin.manage_organizers.event_contributors_explanation") }}
                        </p>
                        <div class="buttons">
                            <button id="invite-organizer-button" class="button is-primary" @click="openAddOrganizerModal">
                                <span class="icon">
                                    <i class="fas fa-user-plus"></i>
                                </span>
                                <span>{{ translate("admin.manage_organizers.invite_organizer") }}</span>
                            </button>
                        </div>

                        <Notification v-if="federationId" class="is-info">
                            <span class="icon">
                                <i class="fas fa-info-circle"></i>
                            </span>
                            <span
                                v-if="canManageFederation"
                                v-html="
                                    translate('admin.manage_organizers.federation_staff_nudge', false, [
                                        urls.federations().federationMembersPage(federationId),
                                    ])
                                "
                            ></span>
                            <span v-else>{{ translate("admin.manage_organizers.federation_staff_nudge_no_access") }}</span>
                        </Notification>
                        <Notification v-else class="is-warning">
                            <span class="icon">
                                <i class="fas fa-exclamation-triangle"></i>
                            </span>
                            <span>
                                {{ translate("admin.manage_organizers.no_federation_assignment") }}
                            </span>
                        </Notification>
                    </div>
                </div>

                <template v-if="eventOrganizers.length > 0">
                    <div class="columns">
                        <div class="column">
                            <p class="mb-3 is-size-5">{{ translate("admin.manage_organizers.organizers_of_event") }}</p>

                            <table class="table is-hoverable is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{{ translate("common.first_name_and_last_name") }}</th>
                                        <th>{{ translate("form.email") }}</th>
                                        <th>{{ translate("admin.manage_organizers.role") }}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(eventOrganizer, index) in eventOrganizers" :key="eventOrganizer.id">
                                        <td>{{ index + 1 }}</td>
                                        <td class="clarity-mask">{{ eventOrganizer.name }}</td>
                                        <td class="clarity-mask">{{ eventOrganizer.email }}</td>
                                        <template v-if="editingId === eventOrganizer.id">
                                            <td>
                                                <div class="select">
                                                    <select v-model="editRole">
                                                        <option :value="EVENT_USER_ROLE_FULL_ORGANIZER">
                                                            {{ translate("admin.manage_organizers.organizer_role") }}
                                                        </option>
                                                        <option :value="EVENT_USER_ROLE_TECHNICIAN">
                                                            {{ translate("admin.manage_organizers.technician_role") }}
                                                        </option>
                                                        <option :value="EVENT_USER_ROLE_STAFF">
                                                            {{ translate("admin.manage_organizers.staff_role") }}
                                                        </option>
                                                    </select>
                                                </div>
                                                <StaffBundlePicker
                                                    v-if="String(editRole) === EVENT_USER_ROLE_STAFF"
                                                    v-model="editBundles"
                                                />
                                            </td>
                                            <td>
                                                <div class="buttons">
                                                    <LoadingButton
                                                        :is-loading="savingInProgress === eventOrganizer.id"
                                                        :disabled="!canSaveEdit"
                                                        class="button is-small is-success"
                                                        @click="saveEdit(eventOrganizer)"
                                                    >
                                                        <span class="icon">
                                                            <i class="fas fa-check"></i>
                                                        </span>
                                                        <span>{{ translate("common.save") }}</span>
                                                    </LoadingButton>
                                                    <button class="button is-small is-danger" @click="cancelEdit">
                                                        {{ translate("common.cancel") }}
                                                    </button>
                                                </div>
                                            </td>
                                        </template>
                                        <template v-else>
                                            <td>
                                                <span>{{ roleLabel(eventOrganizer.role) }}</span>
                                                <div v-if="String(eventOrganizer.role) === EVENT_USER_ROLE_STAFF" class="mt-1">
                                                    <span
                                                        v-for="bundle in bundlesToOptions(eventOrganizer.bundles || [])"
                                                        :key="bundle.key"
                                                        class="tag is-info is-light mr-1"
                                                    >
                                                        {{ bundle.label }}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div class="buttons">
                                                    <button class="button is-small" @click="startEdit(eventOrganizer)">
                                                        <span class="icon">
                                                            <i class="fas fa-pen"></i>
                                                        </span>
                                                        <span>{{ translate("common.edit") }}</span>
                                                    </button>
                                                    <LoadingButton
                                                        :is-loading="removingInProgress === eventOrganizer.id"
                                                        class="button is-small is-danger"
                                                        @click="removeOrganizer(eventOrganizer.id)"
                                                    >
                                                        <span class="icon">
                                                            <i class="fas fa-trash"></i>
                                                        </span>
                                                        <span>{{ translate("common.delete") }}</span>
                                                    </LoadingButton>
                                                </div>
                                            </td>
                                        </template>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>
                <div class="columns" v-else>
                    <div class="column is-offset-3 is-6">
                        <Notification class="is-warning">
                            <span class="icon">
                                <i class="fas fa-exclamation-triangle"></i>
                            </span>
                            <span>{{ translate("admin.manage_organizers.no_organizers") }}</span>
                        </Notification>
                    </div>
                </div>
            </template>
            <div v-else class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </div>

        <AddOrganizerModal
            ref="addOrganizerModal"
            :event-id="eventId"
            :federation-id="federationId"
            :can-manage-federation="canManageFederation"
        />
    </section>
</template>

<script setup lang="ts">
    import { ref, computed, nextTick, onMounted } from "vue";
    import { storeToRefs } from "pinia";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import Notification from "../Common/Notification.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import StaffBundlePicker from "../Common/StaffBundlePicker.vue";
    import AddOrganizerModal from "./AddOrganizerModal.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import urls from "../Urls/urls";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { useOrganizersStore } from "./organizersStore";
    import { useFeatureAnnouncement } from "../Common/FeatureAnnouncements/useFeatureAnnouncement";
    import manageEventOrganizersApi, { EventOrganizer } from "../Api/manageEventOrganizers";
    // EventUserRole is an int-backed PHP enum, so `eventOrganizer.role`/`editRole` values coming
    // from the API and the native <select> are numbers, while the EVENT_USER_ROLE_* constants are
    // kept as strings (matching FederationRole's string-backed convention). String() below is a
    // real coercion, not style — unlike the federation screen, which needs none since
    // FederationRole is string-backed end to end.
    import {
        EVENT_USER_ROLE_FULL_ORGANIZER,
        EVENT_USER_ROLE_TECHNICIAN,
        EVENT_USER_ROLE_STAFF,
        bundlesToOptions,
        Bundle,
    } from "../Common/consts";

    const props = defineProps<{ eventId: number; federationId: string; canManageFederation: boolean; currentUserId: number }>();

    const organizersStore = useOrganizersStore();
    const { organizers: eventOrganizers } = storeToRefs(organizersStore);
    organizersStore.eventId = props.eventId;

    const initDone = ref(false);
    const removingInProgress = ref<number | false>(false);
    const savingInProgress = ref(0);
    const editingId = ref(0);
    const editRole = ref<string | null>(null);
    const editBundles = ref<Bundle[]>([]);

    const canSaveEdit = computed(() => String(editRole.value) !== EVENT_USER_ROLE_STAFF || editBundles.value.length > 0);

    const addOrganizerModal = ref<ComponentExposed<typeof AddOrganizerModal> | null>(null);

    function openAddOrganizerModal(): void {
        addOrganizerModal.value?.open();
    }

    function refreshOrganizers(): Promise<void> {
        return organizersStore.refreshOrganizersListing().catch(handleError);
    }

    function roleLabel(role: string): string {
        if (String(role) === EVENT_USER_ROLE_FULL_ORGANIZER) {
            return translate("admin.manage_organizers.organizer_role");
        }
        if (String(role) === EVENT_USER_ROLE_TECHNICIAN) {
            return translate("admin.manage_organizers.technician_role");
        }
        return translate("admin.manage_organizers.staff_role");
    }

    function startEdit(eventOrganizer: EventOrganizer): void {
        editingId.value = eventOrganizer.id;
        editRole.value = eventOrganizer.role;
        editBundles.value = eventOrganizer.bundles || [];
    }

    function cancelEdit(): void {
        editingId.value = 0;
    }

    function saveEdit(eventOrganizer: EventOrganizer): Promise<void> {
        savingInProgress.value = eventOrganizer.id;

        return organizersStore
            .updateOrganizer(eventOrganizer.id, editRole.value as string, editBundles.value)
            .then(() => {
                successToast(translate("admin.manage_organizers.organizer_updated"));
                editingId.value = 0;
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = 0;
            });
    }

    function removeOrganizer(eventUserId: number): Promise<void> | undefined {
        if (!confirm(translate("admin.manage_organizers.sure_to_remove"))) {
            return;
        }

        removingInProgress.value = eventUserId;

        return manageEventOrganizersApi
            .removeOrganizer(props.eventId, eventUserId)
            .then(() => {
                successToast(translate("admin.manage_organizers.organizer_removed"));
                return refreshOrganizers();
            })
            .catch(handleError)
            .finally(() => {
                removingInProgress.value = false;
            });
    }

    onMounted(() => {
        initDone.value = false;

        return refreshOrganizers().finally(() => {
            initDone.value = true;

            // The invite button is behind v-if="initDone", so wait for the DOM update.
            nextTick(() => {
                useFeatureAnnouncement().showEligible(props.currentUserId, [
                    { key: "manage_organizers_staff_roles", element: "#invite-organizer-button" },
                ]);
            });
        });
    });
</script>

<style scoped></style>
